import { captureException, addBreadcrumb } from "@shared/lib/sentry"

import { REST_API_PATH } from '../config'
import CommonApiError from '../errors/CommonApiError'


export interface ApiRequestOptions extends Omit<RequestInit, 'body'> {
  jsonBody?: unknown;
  skipBreadcrumb?: boolean;
}

async function apiFetchClient<T>(
    path: string,
    options: ApiRequestOptions = {},
): Promise<T> {
    const url = `${REST_API_PATH}${path}`
    const method = options.method ?? 'POST'

    // Sentry breadcrumb before request
    if (!options.skipBreadcrumb) {
        addBreadcrumb({
            type: 'http',
            category: 'api',
            level: 'info',
            message: `Request → ${method} ${url}`,
            data: { url, method, body: options.jsonBody },
        })
    }

    try {
        const response = await fetch(url, {
            ...options,
            headers: {
                'Content-Type': 'application/json',
                ...(options.headers ?? {}),
            },
            body: options.jsonBody !== undefined ? JSON.stringify(options.jsonBody) : undefined,
        })

        const result = await response.json()

        if (!response.ok) {
            const error = new CommonApiError(result?.detail, `HTTP ${response.status}`)
            captureException(error, { url, status: response.status, response: result })
            throw error
        }

        // Sentry breadcrumb after response
        if (!options.skipBreadcrumb) {
            addBreadcrumb({
                type: 'http',
                category: 'api',
                level: 'info',
                message: `Response ← ${method} ${url}`,
                data: result,
            })
        }

        return result as T
    } catch (error: unknown) {
        captureException(error, { url, method })
        throw error
    }
}

export default apiFetchClient
